import { useEffect, useRef, useState } from 'react';
import { CombatAudioEngine, StepSequencer } from '../game/audio/r2/CombatAudioEngine';
import { CombatDirector, type CombatKillKind } from '../game/audio/r2/CombatDirector';
import { R2InputController } from '../game/input/r2/R2InputController';
import { VeilSkimmerController } from '../game/input/r2/VeilSkimmerController';
import {
  loadLayerStack,
  loadR2Index,
  type R2Index,
  type R2IndexAsset,
} from '../game/render/layerstack/LayerStackLoader';
import type { LayerStackSprite } from '../game/render/layerstack/LayerStackSprite';
import {
  FxDirector,
  FX_BULLET_ORDINARY,
  FX_BULLET_PIERCING,
  FX_SLUGS,
  ENEMY_DYING_STATE,
  PLAYER_DAMAGED_STATE,
  PLAYER_DAMAGED_FREEZE_MS,
  PLAYER_DAMAGED_TRAUMA,
  type FxAudioSink,
} from '../game/render/r2/fx/FxDirector';
import { PlayerStateArbiter } from '../game/render/r2/fx/PlayerStateArbiter';
import type { ProjectileImpact } from '../game/render/r2/fx/Projectiles';
import { PLAYER_DAMAGED_FLASH_MS } from '../game/render/r2/fx/StateFlash';
import { R2Renderer, R2_BAND_SPEEDS, R2_HEIGHT, R2_WIDTH, type BandWrapMode } from '../game/render/r2/R2Renderer';

/**
 * Dev harness for the R2 layer-stack path (?r2=harness) — the three workstreams
 * in one loop. R2Renderer draws the parallax bands, VEIL / PRISM and one demo
 * enemy; the input path flies VEIL; the FX director owns the shots, sparks,
 * shake and hit stop. Nothing here is part of the game runtime: ?r2=1 runs the
 * real game on R2GameRenderer, and ?r2=0 (or no token) runs it on the old 16x16
 * path.
 *
 * Audio is the combat engine plus its combo director: the FX events are the
 * triggers (an FX beat that changes no sprite state was silent before), and the
 * chain the director keeps is what decides whether a kill is a small burst, a
 * medium explosion or a phase ignition.
 *
 * The ship's state has two would-be writers (held fire from the controller, the
 * timed `damaged` override from FX), so neither writes it: PlayerStateArbiter
 * resolves them and writes once per frame. See its header for why.
 */
const BAND_SLUGS = ['01-stage1-far', '02-stage1-middle', '03-stage1-near'] as const;

interface BandPlacement {
  /** Top edge in logical pixels; omitted means bottom-aligned. */
  readonly y?: number;
  readonly wrapMode?: BandWrapMode;
}

/**
 * Per-band placement, parallel to BAND_SLUGS. The bands are opaque rectangles,
 * so bottom-aligning all three left a 3 px background stripe above the 537 px
 * far band: pinning it to y=0 covers the top instead and hides the 3 px it
 * loses under the two nearer bands. Every band wraps by mirroring so the
 * vertical wrap seam stops reading as a repeat.
 */
const BAND_PLACEMENTS: readonly BandPlacement[] = [
  { y: 0, wrapMode: 'mirror' },
  { wrapMode: 'mirror' },
  { wrapMode: 'mirror' },
];
const ACTOR_SLUGS = ['veil-skimmer', 'prism-node'] as const;
const ACTOR_ROLES = ['ship', 'prism'] as const;
const SHIP_INDEX = 0;
/** RT is the runtime rung: at R2 the actors are wider than the frame and the gameplay demo cannot read. */
const PREFERRED_RUNG = 'RT';
const SCROLL_PX_PER_SECOND = 42;
const EDGE_MARGIN = 16;
const MAX_FRAME_MS = 100;
const MASTER_LABEL = '(master)';
const MS_PER_SECOND = 1000;
/** Both actors are authored facing away from the demo's reading direction. */
const ACTORS_MIRRORED = true;

const ENEMY_SLUG = '22-indexer';
const ENEMY_ROLE = 'enemy';
const ENEMY_IDLE_STATE = 'neutral';
const ENEMY_RESPAWN_MS = 1200;
const ENEMY_HITS_TO_KILL = 5;
/** One shot on the press, then this cadence while the button stays down. */
const AUTOFIRE_INTERVAL_MS = 180;
const SHAKE_DEMO_TRAUMA = 0.5;
const SFX_VOLUME_STEP = 0.1;
const SFX_VOLUME_MIN = 0;
const SFX_VOLUME_MAX = 1;
/** A phase ignition is a beat: the screen agrees with the cue that just fired. */
const PHASE_ENTRY_TRAUMA = 0.25;
const NORMAL_PHASE = 'normal';
const OVERDRIVE_PHASE = 'overdrive';
/** The live counters change every frame; the panel is republished on a slower clock. */
const COUNTER_PUBLISH_MS = 100;
const PERCENT = 100;

const KEY_BGM = 'b';
const KEY_DAMAGED = 'h';
const KEY_PIERCE = 'g';
const KEY_KILL = 'k';
const KEY_SHAKE = 'l';
const KEY_MUTE = 'n';
const KEY_PULSE = 'p';
const KEY_RUNG = 'r';
const KEY_VOLUME_DOWN = '[';
const KEY_VOLUME_UP = ']';
const KEY_SCALE_1 = '1';
const KEY_SCALE_2 = '2';

const DEMO_KEYS = [
  { key: KEY_PIERCE, label: 'pierce shot — piercing-bullet · armor impact on arrival (shake + 40ms hit stop)' },
  { key: KEY_DAMAGED, label: 'player damaged — arbiter override 280ms · trauma 0.35 · 60ms hit stop' },
  { key: KEY_KILL, label: 'enemy kill — dying · core-hit · kill burst · trauma 0.45 · 100ms hit stop · respawn 1200ms' },
  { key: KEY_SHAKE, label: 'shake only — trauma 0.50, isolates the camera feel' },
  { key: KEY_BGM, label: 'BGM — phase-aware step sequencer, ducked by every impact' },
] as const;

interface ActorStatus {
  readonly slug: string;
  readonly role: string;
  readonly rung: string;
  readonly state: string;
  readonly facing: string;
  readonly mirrored: boolean;
  readonly pulse: string;
}

interface AudioStatus {
  readonly context: string;
  readonly muted: boolean;
  readonly sfx: number;
  readonly bgm: boolean;
  readonly active: number;
  readonly limit: number;
  readonly stolen: number;
  readonly dropped: number;
}

interface ComboStatus {
  readonly combo: number;
  readonly phase: string;
  readonly multiplier: number;
  readonly windowSeconds: number;
  readonly score: number;
  readonly event: string;
}

interface ShipStatus {
  readonly x: number;
  readonly y: number;
  readonly source: string;
  readonly base: string;
  readonly override: string;
  readonly overrideMs: number;
  readonly transitions: number;
}

interface HarnessStatus {
  readonly ready: boolean;
  readonly error: string;
  readonly rung: string;
  readonly rungs: readonly string[];
  readonly scale: number;
  readonly pulsing: boolean;
  readonly renderer: string;
  readonly actors: readonly ActorStatus[];
  readonly bands: readonly string[];
  readonly ship: ShipStatus;
  readonly audio: AudioStatus;
  readonly combat: ComboStatus;
  readonly trauma: number;
  readonly frozenMs: number;
  readonly particles: number;
  readonly projectiles: number;
  readonly hits: number;
}

const INITIAL_AUDIO: AudioStatus = {
  context: 'suspended',
  muted: false,
  sfx: 0,
  bgm: false,
  active: 0,
  limit: 0,
  stolen: 0,
  dropped: 0,
};

const INITIAL_COMBAT: ComboStatus = {
  combo: 0,
  phase: NORMAL_PHASE,
  multiplier: 1,
  windowSeconds: 0,
  score: 0,
  event: '',
};

const INITIAL_SHIP: ShipStatus = {
  x: 0,
  y: 0,
  source: 'keyboard',
  base: 'cruise',
  override: '',
  overrideMs: 0,
  transitions: 0,
};

const INITIAL_STATUS: HarnessStatus = {
  ready: false,
  error: '',
  rung: PREFERRED_RUNG,
  rungs: [],
  scale: 1,
  pulsing: true,
  renderer: '',
  actors: [],
  bands: [],
  ship: INITIAL_SHIP,
  audio: INITIAL_AUDIO,
  combat: INITIAL_COMBAT,
  trauma: 0,
  frozenMs: 0,
  particles: 0,
  projectiles: 0,
  hits: 0,
};

function rungNames(index: R2Index, slugs: readonly string[]): string[] {
  const names = new Set<string>();
  for (const slug of slugs) {
    const asset = index.assets.find((candidate) => candidate.slug === slug);
    if (asset) for (const rung of asset.rungs) names.add(rung.rung);
  }
  return [...names].sort();
}

function findAsset(index: R2Index, slug: string): R2IndexAsset | null {
  return index.assets.find((candidate) => candidate.slug === slug) ?? null;
}

/** The asset's own rung when it has one, otherwise its last — a rung may land for some assets first. */
function resolveRung(asset: R2IndexAsset, rung: string): { path: string; rung: string } | null {
  const exact = asset.rungs.find((candidate) => candidate.rung === rung);
  const chosen = exact ?? asset.rungs[asset.rungs.length - 1];
  return chosen ? { path: chosen.path, rung: chosen.rung } : null;
}

export default function R2Harness() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<HarnessStatus>(INITIAL_STATUS);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new R2Renderer();
    const engine = new CombatAudioEngine();
    const input = new R2InputController();
    const arbiter = new PlayerStateArbiter();
    const ship = new VeilSkimmerController({
      bounds: { width: R2_WIDTH, height: R2_HEIGHT },
      stateSink: (state) => arbiter.setBase(state),
    });
    const actors: (LayerStackSprite | null)[] = [null, null];
    const actorRungs: string[] = ['', ''];
    const bandLabels: string[] = [];
    let index: R2Index | null = null;
    let rungs: string[] = [];
    let rung = PREFERRED_RUNG;
    let pulsing = true;
    let scale = 1;
    let disposed = false;
    let unlocking = false;
    let frame = 0;
    let lastTime = 0;
    let error = '';
    let enemy: LayerStackSprite | null = null;
    let enemyRung = '';
    let enemyRespawnMs = 0;
    let enemyHits = 0;
    let director: FxDirector | null = null;
    let autofireCooldownMs = 0;
    let inputSource = 'keyboard';
    let counterClockMs = 0;
    /** buildScene is async and `r` can retrigger it; a stale run must not publish or install. */
    let sceneToken = 0;
    let lastEvent = '';
    let bgmRunning = false;
    /** `k` destroys a boss part in overdrive; a shot-out enemy is always a medium destroy. */
    let pendingKillKind: CombatKillKind | null = null;

    const combat = new CombatDirector(engine, {
      onEvent: (label) => { lastEvent = label; },
      onPhaseEnter: (phase) => {
        if (phase === NORMAL_PHASE) return;
        renderer.addTrauma(PHASE_ENTRY_TRAUMA);
      },
    });
    const sequencer = new StepSequencer(engine, () => combat.phaseInfo());

    const actorStatus = (slug: string, role: string, assetRung: string, sprite: LayerStackSprite | null): ActorStatus => ({
      slug,
      role,
      rung: assetRung,
      state: sprite?.stateName ?? MASTER_LABEL,
      facing: sprite?.facing ?? '',
      mirrored: sprite?.mirror ?? false,
      pulse: sprite === null ? '' : sprite.pulse === null ? 'none (pulse: null)' : `${sprite.pulse.length}f @ 8fps`,
    });

    /** Polled on the panel's own clock: the allocator changes far too often to push. */
    const audioStatus = (): AudioStatus => {
      const stats = engine.allocator.stats();
      return {
        context: engine.state,
        muted: engine.muted,
        sfx: engine.sfxVolume,
        bgm: bgmRunning,
        active: stats.active,
        limit: stats.limit,
        stolen: stats.stolen,
        dropped: stats.dropped,
      };
    };

    const combatStatus = (): ComboStatus => ({
      combo: combat.combo,
      phase: combat.phase,
      multiplier: combat.multiplier(),
      windowSeconds: combat.windowRemainingSeconds,
      score: combat.score,
      event: lastEvent,
    });

    const publish = (): void => {
      if (disposed) return;
      setStatus({
        ready: actors[0] !== null && actors[1] !== null && enemy !== null && director !== null,
        error,
        rung,
        rungs,
        scale,
        pulsing,
        renderer: renderer.rendererName,
        actors: [
          actorStatus(ACTOR_SLUGS[0], ACTOR_ROLES[0], actorRungs[0] ?? '', actors[0] ?? null),
          actorStatus(ACTOR_SLUGS[1], ACTOR_ROLES[1], actorRungs[1] ?? '', actors[1] ?? null),
          actorStatus(ENEMY_SLUG, ENEMY_ROLE, enemyRung, enemy),
        ],
        bands: [...bandLabels],
        ship: {
          x: Math.round(ship.x),
          y: Math.round(ship.y),
          source: inputSource,
          base: arbiter.base,
          override: arbiter.overrideState,
          overrideMs: Math.max(0, Math.round(arbiter.overrideRemainingMs)),
          transitions: arbiter.transitions,
        },
        audio: audioStatus(),
        combat: combatStatus(),
        trauma: renderer.trauma,
        frozenMs: director?.timebase.frozen ?? 0,
        particles: director?.liveParticles ?? 0,
        projectiles: director?.liveProjectiles ?? 0,
        hits: enemyHits,
      });
    };

    /** Browsers only start a context inside a gesture; every key and pointer press retries. */
    const ensureUnlocked = (): void => {
      if (unlocking || engine.state === 'running') return;
      unlocking = true;
      void engine
        .unlock()
        .catch((caught: unknown) => {
          error = caught instanceof Error ? caught.message : String(caught);
        })
        .finally(() => {
          unlocking = false;
          publish();
        });
    };

    /**
     * The FX events are the audio triggers. Each landed hit advances the chain,
     * so the ladder the director climbs is the player's own rate of fire; the
     * pierce landing changes no sprite state, which is exactly why it needs its
     * own voice here.
     */
    const audioSink: FxAudioSink = {
      shot: () => engine.shot(),
      pierceShot: () => engine.shot(),
      // Silent on purpose: the hit that spawned this spark rings the chain below.
      impactSmall: () => undefined,
      impactArmor: () => engine.eliteBreak(),
      enemyHit: () => combat.kill(1, 'normal'),
      enemyKill: () => {
        const kind = pendingKillKind ?? 'elite';
        pendingKillKind = null;
        combat.kill(1, kind);
      },
      playerDamaged: () => combat.breakCombo('PLAYER HIT'),
    };

    /** The sequencer needs a live context, and the press that asks for it may be the gesture that unlocks it. */
    const toggleBgm = (): void => {
      if (bgmRunning) {
        sequencer.stop();
        bgmRunning = false;
        publish();
        return;
      }
      void engine
        .unlock()
        .then(() => {
          sequencer.start();
          bgmRunning = sequencer.running;
        })
        .catch((caught: unknown) => {
          error = caught instanceof Error ? caught.message : String(caught);
        })
        .finally(() => publish());
    };

    const stepSfxVolume = (up: boolean): void => {
      const next = engine.sfxVolume + (up ? SFX_VOLUME_STEP : -SFX_VOLUME_STEP);
      engine.setSfxVolume(Math.max(SFX_VOLUME_MIN, Math.min(SFX_VOLUME_MAX, next)));
      publish();
    };

    const applyPulse = (): void => {
      for (const sprite of renderer.bandSprites) sprite.setPulsing(pulsing);
      for (const sprite of renderer.actorSprites) sprite.setPulsing(pulsing);
    };

    /** Point test against the enemy's box — enough for a demo, and the tip is a point. */
    const hitEnemy = (x: number, y: number): boolean => {
      const target = enemy;
      if (!target) return false;
      if (target.stateName === ENEMY_DYING_STATE) return false;
      if (x < target.x || x > target.x + target.targetWidth) return false;
      return y >= target.y && y <= target.y + target.targetHeight;
    };

    const killEnemy = (target: LayerStackSprite, x: number, y: number): void => {
      const fx = director;
      if (!fx) return;
      enemyHits = 0;
      fx.enemyKill(target, x, y);
      enemyRespawnMs = ENEMY_RESPAWN_MS;
    };

    const onImpact = (impact: ProjectileImpact): void => {
      const fx = director;
      if (!fx) return;
      // The pierce demo reads as armour: it never counts toward the kill.
      if (impact.slug === FX_BULLET_PIERCING) {
        fx.impactArmor(impact.x, impact.y);
        return;
      }
      const target = enemy;
      if (!target) {
        fx.impactSmall(impact.x, impact.y);
        return;
      }
      enemyHits += 1;
      if (enemyHits < ENEMY_HITS_TO_KILL) {
        fx.enemyHit(target, impact.x, impact.y);
        return;
      }
      killEnemy(target, impact.x, impact.y);
    };

    /** attachSprite seeds cruise and clears the mirror, so the demo facing is re-applied after it. */
    const bindShip = (sprite: LayerStackSprite, x: number, y: number): void => {
      ship.attachSprite(sprite, x, y);
      sprite.setMirror(ACTORS_MIRRORED);
      arbiter.reset();
      arbiter.apply(sprite);
    };

    const buildScene = async (): Promise<void> => {
      const catalogue = index;
      if (!catalogue) return;
      sceneToken += 1;
      const token = sceneToken;
      const stale = (): boolean => disposed || token !== sceneToken;
      ship.detachSprite();
      arbiter.reset();
      director = null;
      enemy = null;
      enemyRung = '';
      enemyRespawnMs = 0;
      enemyHits = 0;
      autofireCooldownMs = 0;
      renderer.clearScene();
      actors[0] = null;
      actors[1] = null;
      bandLabels.length = 0;
      for (let i = 0; i < BAND_SLUGS.length; i += 1) {
        const asset = findAsset(catalogue, BAND_SLUGS[i]!);
        const resolved = asset ? resolveRung(asset, rung) : null;
        if (!asset || !resolved) {
          error = `band ${BAND_SLUGS[i]!} missing from the r2 index — run: bun run assets:r2`;
          continue;
        }
        await renderer.addBand({ rungPath: resolved.path, speed: R2_BAND_SPEEDS[i] ?? 1, ...(BAND_PLACEMENTS[i] ?? {}) });
        if (stale()) return;
        bandLabels.push(`${BAND_SLUGS[i]!} ${resolved.rung} x${(R2_BAND_SPEEDS[i] ?? 1).toFixed(2)}`);
      }
      for (let i = 0; i < ACTOR_SLUGS.length; i += 1) {
        const asset = findAsset(catalogue, ACTOR_SLUGS[i]!);
        const resolved = asset ? resolveRung(asset, rung) : null;
        if (!asset || !resolved) {
          error = `actor ${ACTOR_SLUGS[i]!} missing from the r2 index — run: bun run assets:r2`;
          continue;
        }
        const sprite = await renderer.addActor({ rungPath: resolved.path, x: 0, y: 0, mirror: ACTORS_MIRRORED });
        if (stale()) return;
        const x = i === SHIP_INDEX ? EDGE_MARGIN : R2_WIDTH - sprite.targetWidth - EDGE_MARGIN;
        const y = Math.round((R2_HEIGHT - sprite.targetHeight) / 2);
        sprite.setPixelPosition(x, y);
        actors[i] = sprite;
        actorRungs[i] = resolved.rung;
        // After the placement above: bindShip re-seeds the controller's float position from it.
        if (i === SHIP_INDEX) bindShip(sprite, x, y);
      }
      const enemyAsset = findAsset(catalogue, ENEMY_SLUG);
      const enemyResolved = enemyAsset ? resolveRung(enemyAsset, rung) : null;
      if (!enemyAsset || !enemyResolved) error = `enemy ${ENEMY_SLUG} missing from the r2 index — run: bun run assets:r2`;
      else {
        // Authored facing left, never mirrored: it must face the player, who is mirrored right.
        const sprite = await renderer.addActor({ rungPath: enemyResolved.path, x: 0, y: 0, state: ENEMY_IDLE_STATE });
        if (stale()) return;
        sprite.setPixelPosition(
          Math.round((R2_WIDTH - sprite.targetWidth) / 2),
          Math.round((R2_HEIGHT - sprite.targetHeight) / 2),
        );
        enemy = sprite;
        enemyRung = enemyResolved.rung;
      }
      const fx = new FxDirector(renderer);
      for (const slug of FX_SLUGS) {
        const asset = findAsset(catalogue, slug);
        const resolved = asset ? resolveRung(asset, rung) : null;
        if (!asset || !resolved) {
          error = `fx ${slug} missing from the r2 index — run: bun run assets:r2`;
          continue;
        }
        const loaded = await loadLayerStack(resolved.path);
        if (stale()) return;
        fx.register(slug, loaded);
      }
      fx.setProjectileTarget(hitEnemy, onImpact);
      fx.setAudioSink(audioSink);
      director = fx;
      applyPulse();
      publish();
    };

    const cycleRung = (): void => {
      if (rungs.length === 0) return;
      const next = rungs[(rungs.indexOf(rung) + 1) % rungs.length];
      if (!next) return;
      rung = next;
      error = '';
      publish();
      void buildScene();
    };

    /** No firing flash: `firing` is a held state owned by the controller through the arbiter. */
    const triggerFire = (slug: string): void => {
      const fx = director;
      const player = actors[SHIP_INDEX];
      if (!fx || !player) return;
      fx.fireProjectileOnly(player, slug);
    };

    /**
     * The FX side of playerDamaged without its StateFlash: the flash goes through
     * the arbiter, which is the only writer of this sprite's state.
     */
    const triggerDamaged = (): void => {
      const fx = director;
      if (!fx) return;
      arbiter.flash(PLAYER_DAMAGED_STATE, PLAYER_DAMAGED_FLASH_MS);
      renderer.addTrauma(PLAYER_DAMAGED_TRAUMA);
      fx.timebase.freeze(PLAYER_DAMAGED_FREEZE_MS);
      // The facade's playerDamaged is bypassed here, so its sink call is too.
      audioSink.playerDamaged();
      publish();
    };

    const triggerKill = (): void => {
      const target = enemy;
      if (!target) return;
      pendingKillKind = combat.phase === OVERDRIVE_PHASE ? 'bossPart' : null;
      killEnemy(target, target.x + target.targetWidth / 2, target.y + target.targetHeight / 2);
      // Cleared here too: killEnemy bails out when the FX director is not built yet.
      pendingKillKind = null;
      publish();
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      // Any key is a gesture, so unlock runs before the dispatch chain can bail out.
      ensureUnlocked();
      // Every harness key is a discrete toggle; auto-repeat must not retrigger one.
      if (event.repeat) return;
      const key = event.key.toLowerCase();
      if (key === KEY_PULSE) { pulsing = !pulsing; applyPulse(); publish(); }
      else if (key === KEY_SCALE_1 || key === KEY_SCALE_2) {
        scale = key === KEY_SCALE_1 ? 1 : 2;
        renderer.setDisplayScale(scale);
        publish();
      } else if (key === KEY_RUNG) cycleRung();
      else if (key === KEY_PIERCE) triggerFire(FX_BULLET_PIERCING);
      else if (key === KEY_DAMAGED) triggerDamaged();
      else if (key === KEY_KILL) triggerKill();
      else if (key === KEY_SHAKE) { renderer.addTrauma(SHAKE_DEMO_TRAUMA); publish(); }
      else if (key === KEY_BGM) toggleBgm();
      else if (key === KEY_MUTE) { engine.toggleMute(); publish(); }
      else if (key === KEY_VOLUME_DOWN || key === KEY_VOLUME_UP) stepSfxVolume(key === KEY_VOLUME_UP);
      else return;
      event.preventDefault();
    };

    const onPointerDown = (): void => ensureUnlocked();

    /** The press fires immediately and only then arms the cadence, so a fast tap is never swallowed. */
    const advanceFire = (deltaMs: number, fire: boolean, firePressed: boolean): void => {
      if (firePressed) {
        triggerFire(FX_BULLET_ORDINARY);
        autofireCooldownMs = AUTOFIRE_INTERVAL_MS;
        return;
      }
      if (!fire) {
        autofireCooldownMs = 0;
        return;
      }
      autofireCooldownMs -= deltaMs;
      if (autofireCooldownMs > 0) return;
      triggerFire(FX_BULLET_ORDINARY);
      autofireCooldownMs = AUTOFIRE_INTERVAL_MS;
    };

    const advanceRespawn = (deltaMs: number): void => {
      if (enemyRespawnMs <= 0) return;
      enemyRespawnMs -= deltaMs;
      if (enemyRespawnMs > 0) return;
      enemyRespawnMs = 0;
      const target = enemy;
      if (!target) return;
      target.setState(ENEMY_IDLE_STATE);
      target.setPulsing(pulsing);
    };

    const publishCounters = (rawMs: number): void => {
      counterClockMs += rawMs;
      if (counterClockMs < COUNTER_PUBLISH_MS) return;
      counterClockMs = 0;
      publish();
    };

    const tick = (now: number): void => {
      frame = requestAnimationFrame(tick);
      const rawMs = lastTime === 0 ? 0 : Math.min(MAX_FRAME_MS, now - lastTime);
      lastTime = now;
      const fx = director;
      // One scale call per frame: input, ship, fx, scroll and the renderer ride the same clock.
      const deltaMs = fx ? fx.timebase.scale(rawMs) : rawMs;
      const snapshot = input.sample();
      inputSource = snapshot.source;
      ship.update(deltaMs, snapshot);
      advanceFire(deltaMs, snapshot.fire, snapshot.firePressed);
      arbiter.update(deltaMs);
      const player = actors[SHIP_INDEX];
      if (player) arbiter.apply(player);
      if (fx) fx.update(deltaMs);
      advanceRespawn(deltaMs);
      renderer.scrollX += (deltaMs / MS_PER_SECOND) * SCROLL_PX_PER_SECOND;
      renderer.update(deltaMs);
      publishCounters(rawMs);
      renderer.render();
    };

    const boot = async (): Promise<void> => {
      try {
        await renderer.init(canvas);
        if (disposed) return;
        index = await loadR2Index();
        if (disposed) return;
        rungs = rungNames(index, [...BAND_SLUGS, ...ACTOR_SLUGS]);
        if (!rungs.includes(rung)) rung = rungs[rungs.length - 1] ?? rung;
        await buildScene();
        if (disposed) return;
        window.addEventListener('keydown', onKeyDown);
        input.attach(window);
        frame = requestAnimationFrame(tick);
      } catch (caught) {
        error = caught instanceof Error ? caught.message : String(caught);
      }
      publish();
    };

    window.addEventListener('pointerdown', onPointerDown);
    void boot();
    return () => {
      disposed = true;
      director = null;
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
      input.dispose();
      ship.detachSprite();
      if (frame !== 0) cancelAnimationFrame(frame);
      sequencer.stop();
      combat.dispose();
      engine.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <main style={{ padding: '16px', background: '#0b0d12', color: '#dfe5f0', font: '13px/1.5 ui-monospace, Menlo, monospace', minHeight: '100vh' }}>
      <h1 style={{ fontSize: '15px', letterSpacing: '.12em', textTransform: 'uppercase', margin: '0 0 8px' }}>
        LUMINA ARCHIVE — R2 layer-stack harness
      </h1>
      <p style={{ color: '#7d8798', margin: '0 0 4px' }}>
        fly: <b>arrows</b>/<b>WASD</b>/pad-dpad+stick · fire: <b>space</b>/<b>j</b>/pad-A+trigger (hold = autofire {AUTOFIRE_INTERVAL_MS}ms)
      </p>
      <p style={{ color: '#7d8798', margin: '0 0 4px' }}>
        demo: <b>{KEY_PIERCE}</b> pierce · <b>{KEY_DAMAGED}</b> damaged · <b>{KEY_KILL}</b> kill · <b>{KEY_SHAKE}</b> shake
        {' · '}<b>{KEY_PULSE}</b> pulse · <b>{KEY_RUNG}</b> rung · <b>{KEY_SCALE_1}</b>/<b>{KEY_SCALE_2}</b> display scale
      </p>
      <p style={{ color: '#7d8798', margin: '0 0 12px' }}>
        audio: <b>{KEY_BGM}</b> bgm · <b>{KEY_MUTE}</b> mute · <b>{KEY_VOLUME_DOWN}</b>/<b>{KEY_VOLUME_UP}</b> sfx volume
        {' · '}every FX beat is a cue; hits climb the chain (rush 5 · fever 10 · overdrive 20)
      </p>
      <canvas ref={canvasRef} width={R2_WIDTH} height={R2_HEIGHT} style={{ display: 'block', imageRendering: 'pixelated', border: '1px solid #232a38' }} />
      <pre style={{ marginTop: '12px', padding: '9px 11px', background: '#141822', border: '1px solid #232a38', whiteSpace: 'pre-wrap' }}>
        {status.error !== '' ? `error: ${status.error}\n` : ''}
        {`${status.renderer || 'booting…'}  scale ${status.scale}x  logical ${R2_WIDTH}x${R2_HEIGHT}\n`}
        {`rung ${status.rung} of [${status.rungs.join(' ')}]  pulse ${status.pulsing ? 'playing' : 'paused'}  ready ${String(status.ready)}\n`}
        {`bands: ${status.bands.join(' · ') || '—'}\n`}
        {status.actors.map((actor) => (
          `${actor.role}: ${actor.slug} ${actor.rung} state=${actor.state} facing=${actor.facing}`
          + ` mirror=${String(actor.mirrored)} pulse=${actor.pulse}\n`
        )).join('')}
        {`ship: pos=(${status.ship.x},${status.ship.y}) src=${status.ship.source}\n`}
        {`arbiter: base=${status.ship.base} override=${status.ship.override || '—'}`
          + `${status.ship.override === '' ? '' : `(${status.ship.overrideMs}ms)`}`
          + `  transitions=${status.ship.transitions}\n`}
        {`fx: trauma ${Math.round(status.trauma * PERCENT)}%  frozen ${Math.round(status.frozenMs)}ms`
          + `  particles ${status.particles}  projectiles ${status.projectiles}`
          + `  hits ${status.hits}/${ENEMY_HITS_TO_KILL}\n`}
        {`audio: ${status.audio.context} muted=${String(status.audio.muted)} sfx=${status.audio.sfx.toFixed(2)}`
          + ` bgm=${status.audio.bgm ? 'playing' : 'stopped'}`
          + ` voices ${status.audio.active}/${status.audio.limit}`
          + ` stolen ${status.audio.stolen} dropped ${status.audio.dropped}\n`}
        {`combo ${status.combat.combo} phase ${status.combat.phase} ×${status.combat.multiplier.toFixed(1)}`
          + ` window ${status.combat.windowSeconds.toFixed(1)}s  score ${status.combat.score}\n`}
        {`event: ${status.combat.event || '—'}\n`}
        {DEMO_KEYS.map((trigger) => `${trigger.key}: ${trigger.label}\n`).join('')}
      </pre>
    </main>
  );
}
